import React, { useMemo, useState } from 'react';
import { AddFacultyModal } from '../components/faculty/AddFacultyModal';
import { FacultyFilters, type FilterState } from '../components/faculty/FacultyFilters';
import { FacultyTable } from '../components/faculty/FacultyTable';
import type { FacultyMember, FacultyProfileDraft } from '../types/faculty';

interface FacultyDirectoryPageProps {
  faculty: FacultyMember[];
  isInitialLoading?: boolean;
  facultyInviteLink?: string;
  onNavigateToProfile: (id: string) => void;
  onAddFaculty: (newFaculty: FacultyProfileDraft) => Promise<void> | void;
  onBulkStatusUpdate: (ids: string[], status: FacultyMember['status']) => Promise<void> | void;
  onExportFaculty: (ids?: string[]) => void;
  successToast?: string;
}

const initialFilters: FilterState = {
  search: '',
  division: 'All divisions',
  status: 'All statuses',
  position: 'All positions',
  tags: '',
};

export function FacultyDirectoryPage({
  faculty,
  isInitialLoading = false,
  facultyInviteLink,
  onNavigateToProfile,
  onAddFaculty,
  onBulkStatusUpdate: _onBulkStatusUpdate,
  onExportFaculty,
  successToast: _successToast,
}: FacultyDirectoryPageProps) {
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const filteredFaculty = useMemo(() => {
    return faculty.filter((member) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName =
          member.firstName.toLowerCase().includes(searchLower) ||
          member.lastName.toLowerCase().includes(searchLower) ||
          (member.hebrewFirstName ? member.hebrewFirstName.toLowerCase().includes(searchLower) : false) ||
          (member.hebrewLastName ? member.hebrewLastName.toLowerCase().includes(searchLower) : false);

        if (!matchesName) return false;
      }

      if (filters.division !== 'All divisions' && !member.divisions.includes(filters.division)) {
        return false;
      }
      if (filters.status !== 'All statuses' && member.status !== filters.status) return false;
      if (filters.position !== 'All positions' && member.position !== filters.position) return false;

      if (filters.tags) {
        const tagSearch = filters.tags.toLowerCase();
        const hasTag = member.tags.some((tag) => tag.toLowerCase().includes(tagSearch));
        if (!hasTag) return false;
      }

      return true;
    });
  }, [faculty, filters]);

  const handleSelectRow = (id: string, selected: boolean) => {
    setSelectedRows((current) =>
      selected ? Array.from(new Set([...current, id])) : current.filter((rowId) => rowId !== id),
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedRows(selected ? filteredFaculty.map((member) => member.id) : []);
  };

  const handleSaveFaculty = async (newFaculty: FacultyProfileDraft) => {
    await onAddFaculty(newFaculty);
    setIsAddModalOpen(false);
  };

  return (
    <section
      data-testid="faculty-directory"
      data-loading={isInitialLoading ? 'true' : 'false'}
      data-extracted-from="owned:ystnj/faculty/prd-directory"
      className="mx-auto max-w-[1440px] p-6 lg:p-0"
    >
      <div
        data-testid={isInitialLoading ? 'faculty-directory-loading' : 'faculty-directory-loaded'}
      >
        <div className="mb-6">
          {isInitialLoading ? (
            <div
              data-testid="faculty-count-skeleton"
              className="h-3 w-24 animate-pulse rounded bg-muted/70"
            />
          ) : (
            <span data-testid="faculty-count" className="font-title text-xs uppercase tracking-wider text-text-tertiary">
              {filteredFaculty.length} faculty
            </span>
          )}
        </div>

        {isInitialLoading ? (
          <div
            data-testid="faculty-filters-skeleton"
            className="mb-6 rounded-xl border border-border bg-white p-4 shadow-sm"
          >
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="h-10 w-full animate-pulse rounded-lg bg-muted/70 md:max-w-[320px]" />
              <div className="flex gap-3">
                <div className="h-10 w-24 animate-pulse rounded-lg bg-muted/60" />
                <div className="h-10 w-32 animate-pulse rounded-lg bg-muted/70" />
              </div>
            </div>
          </div>
        ) : (
          <FacultyFilters
            onFilterChange={setFilters}
            onAddFaculty={() => setIsAddModalOpen(true)}
            onExport={() => onExportFaculty()}
            filtersVisible={filtersVisible}
            onToggleFilters={() => setFiltersVisible((current) => !current)}
          />
        )}
      </div>

      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          selectedRows.length > 0 ? 'mb-4 max-h-16 opacity-100' : 'mb-0 max-h-0 opacity-0'
        }`}
      >
        <div data-testid="bulk-toolbar" className="flex items-center justify-between rounded-lg border border-primary/20 bg-muted p-3">
          <span data-testid="selection-count" className="text-sm font-medium text-iron-grey">
            {selectedRows.length} faculty member{selectedRows.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border bg-white px-3 py-1.5 text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              Update status
            </button>
            <button
              type="button"
              data-testid="bulk-export"
              onClick={() => onExportFaculty(selectedRows)}
              className="rounded-md border border-border bg-white px-3 py-1.5 text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              Export selected
            </button>
          </div>
        </div>
      </div>

      <FacultyTable
        data={filteredFaculty}
        isInitialLoading={isInitialLoading}
        onSelectFaculty={onNavigateToProfile}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
      />

      <AddFacultyModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveFaculty}
        facultyInviteLink={facultyInviteLink}
      />
    </section>
  );
}
